<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Pagos / Convenio / Historial</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <!--Filtro de fechas-->
          <div class="mb-6">
            <DateRangePicker
              :from-date="fromDate"
              :to-date="toDate"
              @filter="handleDateFilter"
              @clear="handleClearFilter"
              :auto-apply="false"
            />
          </div>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="noDataAvailable" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Sin información disponible
          </div>

          <div v-else-if="sortedData && sortedData.length > 0" class="mb-6">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold">Historial de Cargos de Título/Certificado Pagados:</h2>
              <span class="text-sm text-gray-600">
                {{ sortedData.length }} registro(s) encontrado(s)
              </span>
            </div>
            
            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div 
                v-for="item in sortedData" 
                :key="item.idPeriod"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4"
                :class="isPastDate(item.payment_date) ? '' : 'border-blue-500'"
              >
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Descripción:</div>
                  <div class="text-sm">{{ item.description }}</div>                  
                  
                  <div class="text-sm font-medium text-gray-500">Fecha de Pago:</div>
                  <div class="text-sm font-bold">{{ formatDate(item.payment_date) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Cargo:</div>
                  <div class="text-sm font-bold">{{ formatCurrency(item.charge) }}</div>

                  <div class="text-sm font-medium text-gray-500">Abono:</div>
                  <div class="text-sm font-bold">{{ formatCurrency(item.payment) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Saldo:</div>
                  <div class="text-sm font-bold">{{ formatCurrency(item.balance) }}</div>
                </div>
              </div>
            </div>
            
            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Descripción</th>
                    <th scope="col" class="py-3 pl-4 pr-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Fecha de Pago</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Cargo</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Abono</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Saldo</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                  <tr 
                    v-for="item in sortedData" 
                    :key="item.idPeriod"
                    :class="isPastDate(item.payment_date) ? '' : ''"
                  >
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      {{ item.description }}
                    </td>
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-center">
                      {{ formatDate(item.payment_date) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      {{ formatCurrency(item.charge) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      {{ formatCurrency(item.payment) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      {{ formatCurrency(item.balance) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import { getAgreementTickets } from "@/services/api";

export default {
  components: {
    SidebarMenu,
    DateRangePicker
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      data: null,
      loading: true,
      error: null,
      studentId: null,
      fromDate: '',
      toDate: ''
    };
  },
  computed: {
    // Ordenar los datos por idschedule de mayor a menor
    sortedData() {
      if (!this.data) return null;
      return [...this.data].sort((a, b) => b.idschedule - a.idschedule);
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Método para formatear números como moneda ($0.00)
    formatCurrency(value) {
      if (value === null || value === undefined) return '$0.00';
      return new Intl.NumberFormat('es-MX', { 
        style: 'currency', 
        currency: 'MXN',
        minimumFractionDigits: 2
      }).format(value);
    },
    
    // Método para formatear fechas en formato yyyy-mm-dd
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    },
    
    // Método para determinar si una fecha ya pasó
    isPastDate(dateString) {
      if (!dateString) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Resetear la hora para comparar solo las fechas
      const payment_date = new Date(dateString);
      payment_date.setHours(0, 0, 0, 0);
      return payment_date < today;
    },
    
    // Manejar el filtro de fechas
    handleDateFilter(dateRange) {
      this.fromDate = dateRange.from;
      this.toDate = dateRange.to;
      this.loadData();
    },
    
    // Manejar la limpieza del filtro
    handleClearFilter() {
      this.fromDate = '';
      this.toDate = '';
      this.loadData();
    },
    
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          // Establecer fechas por defecto (últimos 30 días)
          const today = new Date();
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          
          this.fromDate = thirtyDaysAgo.toISOString().split('T')[0];
          this.toDate = today.toISOString().split('T')[0];

          // Cargar datos con el ID extraído
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadData() {
      try {
        this.loading = true;
        this.error = null;
        this.noDataAvailable = false;

        const response = await getAgreementTickets({idstudent: this.studentId, from: this.fromDate, to: this.toDate});
        
        // Verificar si la respuesta tiene datos
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.data = response.data;
        } else {
          // Si api respondió correctamente pero no hay datos
          this.data = [];
          this.noDataAvailable = true;
        }
        
        this.loading = false;
      } catch (error) {
        this.loading = false;
        
        // 404 = URL existe pero no hay datos para este estudiante
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          return;
        }
        
        // Cualquier otro error = problema de conexión/URL/servidor
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>